import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import toast from "react-hot-toast";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";

interface FinanceUploadDocumentsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: (credit_note_image: string, invoice_image: string) => void;
}

export default function FinanceUploadDocumentsModal({ open, setOpen, onSave }: FinanceUploadDocumentsModalProps) {
  const cancelButtonRef = useRef(null);
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();

  const [invoiceImage, setInvoiceImage] = useState<string>("");
  const [creditNoteImage, setCreditNoteImage] = useState<string>("");

  // ✅ separate loaders
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creditNoteLoading, setCreditNoteLoading] = useState(false);

  const handleClose = () => {
    setInvoiceImage("");
    setCreditNoteImage("");
    setOpen(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "invoice" | "credit_note") => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG, WEBP, PDF files are allowed.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      e.target.value = "";
      return;
    }

    if (type === "invoice") setInvoiceLoading(true);
    else setCreditNoteLoading(true);

    try {
      const result = await uploadToS3(file, "finance_upload", "shrink_image");

      if (result?.status && result?.message) {
        toast.success(`${type === "invoice" ? "Invoice" : "Credit Note"} uploaded successfully`);
        if (type === "invoice") setInvoiceImage(result.message);
        else setCreditNoteImage(result.message);
      } else {
        toast.error("Failed to upload image.");
      }
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      if (type === "invoice") setInvoiceLoading(false);
      else setCreditNoteLoading(false);
      e.target.value = "";
    }
  };

  const handleSave = () => {
    if (!invoiceImage || !creditNoteImage) {
      toast.error("Please upload both Invoice and Credit Note.");
      return;
    }
    onSave(creditNoteImage, invoiceImage);
    handleClose();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-6 py-6 text-left shadow-xl transition-all w-[500px]">
                <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
                  <FaTimes size={16} />
                </button>

                <Dialog.Title className="text-base font-semibold text-gray-900 mb-6">
                  Upload Required Documents
                </Dialog.Title>

                {/* Upload Invoice */}
                <div className="flex items-center gap-4 mb-6">
                  <label className="text-sm font-medium text-gray-700 w-1/3">Upload Invoice</label>
                  <div className="flex-1 flex items-center justify-start">
                    {invoiceImage ? (
                      <div className="relative inline-block">
                        {invoiceImage.endsWith(".pdf") ? (
                          <a
                            href={invoiceImage}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-28 h-20 border rounded flex items-center justify-center text-blue-600 text-sm font-medium underline"
                          >
                            View PDF
                          </a>
                        ) : (
                          <img src={invoiceImage} alt="Invoice" className="w-28 h-20 object-contain border rounded" />
                        )}
                        <button
                          onClick={() => setInvoiceImage("")}
                          className="absolute top-0 right-0 bg-white rounded-full text-xs shadow-md p-1"
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <label className="border border-gray-300 rounded-md w-full p-4 flex items-center justify-center gap-2 text-[rgb(54,130,238)] font-medium text-sm cursor-pointer">
                        <FiUpload />
                        <span>{invoiceLoading ? "Uploading..." : "Upload (JPG, PNG, WEBP, PDF ≤ 5MB)"}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp, application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, "invoice")}
                          disabled={invoiceLoading || creditNoteLoading}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Upload Credit Note */}
                <div className="flex items-center gap-4 mb-6">
                  <label className="text-sm font-medium text-gray-700 w-1/3">Upload Credit Note</label>
                  <div className="flex-1 flex items-center justify-start">
                    {creditNoteImage ? (
                      <div className="relative inline-block">
                        {creditNoteImage.endsWith(".pdf") ? (
                          <a
                            href={creditNoteImage}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-28 h-20 border rounded flex items-center justify-center text-blue-600 text-sm font-medium underline"
                          >
                            View PDF
                          </a>
                        ) : (
                          <img
                            src={creditNoteImage}
                            alt="Credit note image"
                            className="w-28 h-20 object-contain border rounded"
                          />
                        )}
                        <button
                          onClick={() => setCreditNoteImage("")}
                          className="absolute top-0 right-0 bg-white rounded-full text-xs shadow-md p-1"
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <label className="border border-gray-300 rounded-md w-full p-4 flex items-center justify-center gap-2 text-[rgb(54,130,238)] font-medium text-sm cursor-pointer">
                        <FiUpload />
                        <span>{creditNoteLoading ? "Uploading..." : "Upload (JPG, PNG, WEBP, PDF ≤ 5MB)"}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp, application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, "credit_note")}
                          disabled={creditNoteLoading || invoiceLoading}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={invoiceLoading || creditNoteLoading}
                    className="px-5 py-2 text-sm bg-[#193A6A] text-white rounded-md"
                  >
                    Save
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
